/**
 * One builder for the full-page card URL, shared by every surface that
 * links to a card.
 *
 * The shape mirrors the legacy FE router (3mote-fe `src/router.jsx`) and
 * is what the four deep-link routes under `src/routes/` parse:
 *
 *   - task:         /{projectCode}/tasks/{shorthand}
 *   - ticket:       /{projectCode}/tickets/{shorthand}
 *   - event:        /events/{id}
 *   - announcement: /announcements/{id}
 *
 * It lives here rather than beside any one caller because three of them
 * now build it — the card modal's shorthand row, the inline work-item
 * chip's NodeView, and the sent-chat-message renderer — and a card link
 * that means something different depending on where you clicked it is
 * precisely the drift `WorkItemChipBody` was extracted to prevent.
 *
 * These paths open the card as its own PAGE. The board deep link
 * (`/board/{projectId}?selectedDeliverable=…`) opens the same card as a
 * MODAL over the board, which is the right destination for something
 * that is already a board interaction (a notification about a card you
 * are working on, a search result within the board) and the wrong one
 * for a reference embedded in prose — following a reference out of a
 * document should land on the referenced thing, not on the board it
 * happens to live on.
 */

import type { DeliverableKind } from '@/api/types-bridge'

/**
 * Recover the project code from a card shorthand.
 *
 * Exact, not a heuristic: the BE mints a shorthand as
 * `${projects.code}-${projects.code_seq}` (see `assignShorthand`), and
 * `projects.code` is CHECK-constrained to `^[A-Z][A-Z0-9]{1,4}$` — so
 * the code can never itself contain the separator, and everything before
 * the last `-` is the code.
 *
 * Needed because the inline chip's payload (`GET /api/cards/:id
 * ?fields=chip`) carries `projectId` but no project code, and the
 * task / ticket route is keyed by code.
 */
export function projectCodeFromShorthand(shorthand: string | null | undefined): string | null {
  if (!shorthand) return null
  const separator = shorthand.lastIndexOf('-')
  if (separator <= 0) return null
  return shorthand.slice(0, separator)
}

export interface CardDeepLinkInput {
  kind: DeliverableKind
  id: string
  /** `MOTE-30`. Required for tasks + tickets; unused by the id-keyed kinds. */
  shorthand?: string | null
  /**
   * The card's project code, when the caller holds an authoritative one.
   * Omit it and the shorthand's own prefix stands in — see
   * `projectCodeFromShorthand` for why that is exact.
   */
  projectCode?: string | null
}

/**
 * Returns null only when a task / ticket is missing the shorthand its
 * URL is keyed by. Callers are expected to degrade rather than render a
 * dead link — the chip surfaces fall back to the board deep link, the
 * modal's shorthand row falls back to plain non-clickable text.
 */
export function buildCardDeepLinkPath(input: CardDeepLinkInput): string | null {
  switch (input.kind) {
    case 'task':
    case 'ticket': {
      const shorthand = input.shorthand
      if (!shorthand) return null
      const projectCode = input.projectCode ?? projectCodeFromShorthand(shorthand)
      if (!projectCode) return null
      const segment = input.kind === 'task' ? 'tasks' : 'tickets'
      return `/${projectCode}/${segment}/${shorthand}`
    }
    case 'event':
      return `/events/${input.id}`
    case 'announcement':
      return `/announcements/${input.id}`
    default:
      return null
  }
}

/** Strict UUID matcher — the same shape the embed node validates its attrs with. */
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i

/**
 * A shorthand exactly as the BE mints it: `projects.code` (CHECK
 * `^[A-Z][A-Z0-9]{1,4}$`) + `-` + `projects.code_seq`.
 */
const SHORTHAND_RE = /^[A-Za-z][A-Za-z0-9]{1,4}-\d{1,9}$/

/** `/{projectCode}/tasks/{shorthand}` and its `tickets` twin. */
const PAGE_ROUTE_RE =
  /^\/([A-Za-z][A-Za-z0-9]{1,4})\/(tasks|tickets)\/([A-Za-z][A-Za-z0-9]{1,4}-\d{1,9})$/

/** How to look a referenced card up. */
export type CardReference =
  | {
      by: 'shorthand'
      /** Uppercased. */
      shorthand: string
      /** Uppercased. Present only when the URL carried one; never sent to the BE. */
      projectCode?: string
      kindHint?: DeliverableKind
    }
  | { by: 'id'; cardId: string; kindHint?: DeliverableKind }

/**
 * The inverse of `buildCardDeepLinkPath`: a URL pointing at a card in
 * THIS workspace → the reference needed to resolve it, else null.
 *
 * Used by the editor's reference auto-detection to decide whether a
 * pasted or typed link is a card link before any network call. Four
 * shapes parse, and every one of them is a URL the app itself produces:
 *
 *   1. `/{code}/tasks/{shorthand}` + `/{code}/tickets/{shorthand}`
 *      — the card's own page; what the chip menu's Copy link emits.
 *   2. `/events/{uuid}` — the event page.
 *   3. `/board/{projectUuid}?selectedDeliverable={shorthand|uuid}`
 *      — the address bar while a card modal is open on a board.
 *   4. `/?selectedDeliverable={shorthand|uuid}` — the same on the
 *      master board.
 *
 * `origin` is INJECTED rather than read from `window` so this stays
 * pure and specs can pin a host. Comparing origins (not hosts) is what
 * makes the check correct on `aeeiee.3mote.com`, `aeeiee-staging…` and
 * `localhost:5173` with no host-parsing: a URL from another tenant,
 * another stage, or another scheme simply isn't this workspace's, and
 * the card ids in it address a different database.
 *
 * The kind and project-code segments are HINTS, never the truth — a
 * `/tasks/` URL whose shorthand now belongs to a ticket still resolves,
 * because the by-shorthand route unions across kinds and the resolved
 * payload's kind wins. Re-coding a project likewise leaves old links
 * working, which is why the code segment is not cross-checked against
 * the shorthand's own prefix.
 *
 * Announcements are deliberately absent: they carry no shorthand, and
 * the iOS + Android chat projections render a work-item chip as its
 * shorthand — an announcement chip would arrive on mobile as the empty
 * string.
 */
export function parseCardDeepLink(href: string, origin: string): CardReference | null {
  let url: URL
  try {
    url = new URL(href)
  } catch {
    // Not an absolute URL at all (a bare domain, a relative path, junk).
    return null
  }
  // `origin` carries the scheme, so this one comparison also rejects an
  // `http://` twin of an `https://` workspace — deliberate: a scheme-less
  // link is marked `http://` by the paste rule, and following it would
  // leave the app.
  if (url.origin !== origin) return null

  // Tolerate one trailing slash on every shape except the root itself.
  const path = url.pathname.length > 1 ? url.pathname.replace(/\/$/, '') : url.pathname

  const page = PAGE_ROUTE_RE.exec(path)
  if (page?.[1] && page[2] && page[3]) {
    return {
      by: 'shorthand',
      shorthand: page[3].toUpperCase(),
      projectCode: page[1].toUpperCase(),
      kindHint: page[2] === 'tasks' ? 'task' : 'ticket',
    }
  }

  const event = /^\/events\/(.+)$/.exec(path)
  if (event?.[1] && UUID_RE.test(event[1])) {
    return { by: 'id', cardId: event[1].toLowerCase(), kindHint: 'event' }
  }

  // Both board shapes address the card through the same search param the
  // board surfaces put there, which holds a shorthand for task / ticket /
  // event and an id when it came from a notification payload.
  const onBoard = /^\/board\/(.+)$/.exec(path)
  const isMaster = path === '/'
  if (isMaster || (onBoard?.[1] && UUID_RE.test(onBoard[1]))) {
    const selected = url.searchParams.get('selectedDeliverable')
    if (!selected) return null
    if (SHORTHAND_RE.test(selected)) {
      return { by: 'shorthand', shorthand: selected.toUpperCase() }
    }
    if (UUID_RE.test(selected)) return { by: 'id', cardId: selected.toLowerCase() }
    return null
  }

  return null
}
